'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import {
  Suspense,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';

import {
  getAuthIdentity,
  removeAuthIdentity,
  setAuth
} from 'services/identity.service';
import {
  LOCAL_STORAGE_KEYS,
  removeLocalStorageValue
} from 'services/localStorage.service';
import { PAGE_NAME, getPageURL } from 'services/partnerPortal.service';

const UIContext = createContext();
const UIProvider = (props) => {
  const router = useRouter();
  const searchParams = useSearchParams();

  const [partnerUser, setPartnerUser] = useState(null);

  const navigateUser = useCallback(
    (pathName) => router.push(pathName || '/'),
    [router]
  );

  const login = useCallback(
    async ({ userDetails, pathName }) => {
      if (userDetails) {
        setAuth(userDetails);
        navigateUser(`/${pathName}`); // TODO: setPartnerUser after setAuth and then navigateUser, so that its easy to read and understand
        setPartnerUser(userDetails);
      }
      router.push(
        getPageURL({
          pageName: PAGE_NAME.LOGIN.label,
          queryParams: { redirectTo: `/${pathName}` }
        })
      );
    },
    [navigateUser, router]
  );

  const redirectAuthenticatedUser = useCallback(() => {
    const redirectTo = searchParams.get('redirectTo');
    const destination = redirectTo
      ? decodeURIComponent(redirectTo)
      : getPageURL({ pageName: PAGE_NAME.HOME.label });

    const url = new URL(destination, window.location.origin);
    searchParams.forEach((value, key) => {
      if (key === 'redirectTo') return;
      if (!url.searchParams.has(key)) {
        url.searchParams.set(key, value);
      }
    });

    router.push(`${url.pathname}${url.search}`);
  }, [router, searchParams]);

  const logout = useCallback(() => {
    removeAuthIdentity();
    removeLocalStorageValue({
      keys: [
        LOCAL_STORAGE_KEYS.DATE_PICKER_VALUE,
        LOCAL_STORAGE_KEYS.DATE_RANGE,
        LOCAL_STORAGE_KEYS.DATE_FILTER_KEY
      ]
    });
    return router.push(getPageURL({ pageName: PAGE_NAME.LOGIN.label }));
  }, [router]);

  useEffect(() => {
    setPartnerUser(getAuthIdentity());
  }, []);

  const value = useMemo(
    () => ({
      login,
      logout,
      partnerUser,
      redirectAuthenticatedUser
    }),
    [login, logout, partnerUser, redirectAuthenticatedUser]
  );

  return (
    <UIContext.Provider
      value={value}
      {...props}
    />
  );
};

const useUIContext = () => {
  const context = useContext(UIContext);
  if (context === undefined) {
    throw new Error('useUIContext must be used within a UIProvider');
  }
  return context;
};

const ManagedUIContextContent = ({ children }) => (
  <UIProvider>{children}</UIProvider>
);

const ManagedUIContext = ({ children }) => (
  // We need to wrap this in Suspense because strict Mode is ON in Next.js 14
  // and useSearchParams() hook will throw an error if not wrapped in Suspense for static pages like 404
  <Suspense fallback={null}>
    <ManagedUIContextContent>{children}</ManagedUIContextContent>
  </Suspense>
);

export { ManagedUIContext, useUIContext };
